import dayjs from "dayjs";
import { Pencil, Trash2, Calendar, ArrowRight, Info } from "lucide-react";

import { TranslatedText } from "@/components/common/text";
import { SelectField, DeliveryDatePicker, Checkbox } from "@/components/common/inputs";
import { LocationDropdown } from "@/components/search";
import { trailerTypes, truckStatuses, TruckStatus } from "@/utils/constants";

const FieldLabel = ({ children }) => (
    <span className="px-[2px] text-[12px] font-medium uppercase text-cm-p-text-secondary">{children}</span>
);

// A step is "blocked" only when it is On route with a delivery date set in the past.
export const isTruckStepBlocked = (edit) => {
    if (!edit) return false;
    if (edit.status !== TruckStatus.LOADED.id) return false;
    if (!edit.deliveryDate) return false;
    return dayjs(edit.deliveryDate).isBefore(dayjs(), "day");
};

// One truck's editable fields inside the Trucks check wizard. Fields that don't apply to the
// selected status are hidden (not disabled), mirroring Add/Edit truck modals.
export const TruckCheckStep = ({
    truck,
    edit,
    onPatch,
    expiredFilters = [],
    activeFiltersCount = 0,
    onEditFilter,
    onDeleteFilter,
    truckListEnabled,
}) => {
    const isOnRoute = edit.status === TruckStatus.LOADED.id;
    const isOffDuty = edit.status === TruckStatus.OFF_DUTY.id;
    const showPublicList = !isOffDuty && truckListEnabled;
    const dateInPast = isTruckStepBlocked(edit);
    // Location and date are always shown; their labels follow the status (matching the Add/Edit
    // truck modals). On route the location is the load destination; off duty it is optional.
    const locationLabel = isOnRoute ? "Load destination" : isOffDuty ? "Location (optional)" : "Current location";
    const dateLabel = isOnRoute ? "Delivery date" : isOffDuty ? "Ready date (optional)" : "Available from (optional)";

    const renderExpiredFilterRow = (filter) => (
        <div
            key={filter.id}
            className="flex w-full shrink-0 items-center overflow-hidden rounded-[8px] border border-[rgba(177,114,62,0.2)] bg-cm-p-container-orange py-[2px] pl-3 pr-px"
        >
            <div className="flex min-w-0 flex-1 items-center gap-4">
                <div className="flex items-center gap-2 text-[14px] font-medium text-cm-p-text-primary">
                    <span className="whitespace-nowrap">
                        {filter.departure?.city}, {filter.departure?.state}
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-cm-p-text-primary" />
                    <span className="whitespace-nowrap">
                        {filter.dropOff ? `${filter.dropOff.city}, ${filter.dropOff.state}` : "Anywhere"}
                    </span>
                </div>
                {filter.availableDateStart && (
                    <span className="flex h-[20px] shrink-0 items-center gap-1 rounded-[6px] bg-[rgba(177,114,62,0.1)] px-[6px] text-cm-p-on-container-orange">
                        <Calendar className="h-[14px] w-[14px]" />
                        <span className="text-[12px] font-medium tracking-[-0.12px]">
                            {dayjs(filter.availableDateStart.slice(0, 10)).format("MMM D")}
                        </span>
                    </span>
                )}
            </div>
            <div className="flex shrink-0 items-center">
                <button
                    type="button"
                    onClick={() => onEditFilter?.(filter)}
                    aria-label="Edit filter"
                    className="flex size-[38px] items-center justify-center rounded-[6px]"
                >
                    <Pencil className="h-4 w-4 text-cm-p-text-secondary" />
                </button>
                <button
                    type="button"
                    onClick={() => onDeleteFilter?.(filter)}
                    aria-label="Delete filter"
                    className="flex size-[38px] items-center justify-center rounded-[6px]"
                >
                    <Trash2 className="h-4 w-4 text-cm-p-on-container-red" />
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col gap-5">
            {/* Read-only truck header: driver name (bold) stacked above the truck number */}
            <div className="flex w-full flex-col gap-1">
                <FieldLabel>
                    <TranslatedText>Truck</TranslatedText>
                </FieldLabel>
                <div className="flex flex-col gap-0.5">
                    <span className="text-[16px] font-medium text-cm-p-text-primary">
                        {truck.driverName || truck.truckName}
                    </span>
                    {truck.driverName && (
                        <span className="text-[14px] text-cm-p-text-secondary">{truck.truckName}</span>
                    )}
                </div>
            </div>

            {/* Trailer type + Status */}
            <div className="flex w-full gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <FieldLabel>
                        <TranslatedText>Trailer type</TranslatedText>
                    </FieldLabel>
                    <SelectField
                        value={trailerTypes.find((t) => t.id === edit.trailerType) ?? trailerTypes[0]}
                        onChange={(opt) => onPatch({ trailerType: opt.id })}
                        options={trailerTypes}
                    />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <FieldLabel>
                        <TranslatedText>Status</TranslatedText>
                    </FieldLabel>
                    <SelectField
                        value={truckStatuses.find((s) => s.id === edit.status) ?? truckStatuses[0]}
                        onChange={(opt) => onPatch({ status: opt.id })}
                        options={truckStatuses}
                    />
                </div>
            </div>

            {/* Location + date (labels are status-conditional) */}
            <div className="flex w-full gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <FieldLabel>
                        <TranslatedText>{locationLabel}</TranslatedText>
                    </FieldLabel>
                    <LocationDropdown
                        name={`tc-location-${truck.truckId ?? truck.truckName}`}
                        value={edit.location}
                        setValue={(loc) => onPatch({ location: loc })}
                        query={edit.locationQuery}
                        setQuery={(q) => onPatch({ locationQuery: q })}
                        label=""
                        theme="truck"
                    />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <FieldLabel>
                        <TranslatedText>{dateLabel}</TranslatedText>
                    </FieldLabel>
                    <DeliveryDatePicker
                        value={edit.deliveryDate}
                        onChange={(date) => onPatch({ deliveryDate: date })}
                        openUpward={false}
                    />
                    {dateInPast && (
                        <div className="mt-1 flex items-center gap-1">
                            <Info className="h-4 w-4 shrink-0 text-cm-p-on-container-red" />
                            <span className="text-[14px] text-cm-p-on-container-red">
                                <TranslatedText>Delivery date is in past</TranslatedText>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Public Truck List visibility */}
            {showPublicList && (
                <Checkbox
                    checked={edit.onTruckList}
                    onChange={(v) => onPatch({ onTruckList: v })}
                    label={<TranslatedText>Visible on the public Truck List</TranslatedText>}
                />
            )}

            {/* Expired filters (actionable) */}
            {expiredFilters.length > 0 && (
                <div className="flex min-h-0 w-full flex-col gap-1">
                    <span className="px-[2px] text-[12px] font-medium uppercase tracking-[-0.12px] text-cm-p-on-container-orange">
                        {expiredFilters.length} <TranslatedText>EXPIRED FILTER</TranslatedText>
                    </span>
                    <div className="flex min-h-0 flex-col gap-1 overflow-y-auto [scrollbar-color:auto] [scrollbar-width:auto]">
                        {expiredFilters.map((filter) => renderExpiredFilterRow(filter))}
                    </div>
                </div>
            )}

            {/* Active filters count */}
            <span className="px-1 text-[12px] font-medium uppercase text-cm-p-text-secondary">
                {activeFiltersCount} <TranslatedText>ACTIVE FILTERS</TranslatedText>
            </span>
        </div>
    );
};
